/** @jsx jsx */
import { jsx } from "@emotion/core";
import * as React from "react";
import { IUploadProps } from "./interfaces";
import { Upload } from "antd";
import ImgCrop from "antd-img-crop";
import { toast } from "../../utils/toast";
import { uploadStyle } from "./styles";
import {
  allowedFileTypes,
  assetExtensionsFromServer,
  imageRenderProp,
  loadingScreenValues,
  UploadTypes,
} from "../../constants/commons";
import { useIsMobile } from "../../hooks/useIsMobile";
import { responsiveStyles } from "../../commons/styles";
import { URL } from "../../API/urlEndpoints";
import removeIcon from "../../assets/remove-icon.svg";
import uploadedDocsIcon from "../../assets/uploaded-docs-logo.svg";
import { useUserContext } from "../../hooks/cookieHooks";
import * as _ from "lodash";
import { browserDownload } from "../../utils/helpers";
import { ShowMessage } from "../../components/ShowMessage";
import API from "../../API/API";

export const UploadComp: React.FC<IUploadProps> = (props: IUploadProps) => {
  const {
    uploadType,
    allowedFilesType,
    onSuccess,
    uploadedFiles,
    removeSelectedAsset,
    allowedFileSize,
  } = props;
  // Calling the SSO Hook to retrieve user information of the logged in user
  const user = useUserContext();

  // State to maintain when the user upload Fails with an error from server
  const [errorWhileUpload, setErrorWhileUpload] = React.useState<boolean>(
    false
  );

  const [isUploading, setIsUploading] = React.useState<boolean>(false);

  // State to maintain the uploaded file name
  const [uploadedFileName, setUploadedFileName] = React.useState<string>();

  const [fileList, setFileList] = React.useState<any[]>();
  const isMobileView = useIsMobile();

  const onChange = React.useCallback(
    (info: any) => {
      if (info.file.status !== "uploading") {
        setIsUploading(false);
      }
      if (info.file.status === "uploading") {
        setIsUploading(true);
        console.log("Upload started");
      }
      if (info.file.status === "done") {
        setIsUploading(false);
        let uploadedFileObj = {
          ...{ thumbUrl: info.file.thumbUrl },
          ...{ fileName: info.file.name },
          ...info.file.response.data,
        };
        uploadedFileObj =
          uploadType === UploadTypes.SELFIE_VIDEO
            ? { ...uploadedFileObj, ...info.file.response.data.document }
            : { ...uploadedFileObj };
        onSuccess && onSuccess(uploadedFileObj);
        setFileList(uploadedFileObj);
        toast.success(`${info.file.name} file uploaded successfully`);
      } else if (info.file.status === "error") {
        setIsUploading(false);
        setErrorWhileUpload(true);
        toast.error(`${info.file.name} file upload failed.`);
      }
    },
    [onSuccess, uploadType]
  );

  // Function which saves the uploaded PROFILE PICTURE
  React.useEffect(() => {
    uploadedFiles &&
      Object.keys(uploadedFiles).length > 0 &&
      uploadedFiles[0].uploadType === UploadTypes.PROFILE_PICTURE &&
      setFileList(uploadedFiles[0]);
  }, [uploadedFiles]);

  // Function which checks whether the uploaded file has image or not. If yes, then it prevents user from uploading another one
  const isImagePresent = React.useMemo<any>(() => {
    return fileList;
  }, [fileList]);

  const beforeUpload = React.useCallback(
    (file: any) => {
      let isFileSizeExceeded = false;
      if (allowedFileSize) {
        isFileSizeExceeded = !(file.size / 1024 / 1024 <= allowedFileSize);
      }
      if (isFileSizeExceeded) {
        toast.error("File size exceeded");
        return false;
      } else {
        setUploadedFileName(file.name);
        return true;
      }
    },
    [allowedFileSize]
  );

  const onPreview = async (file: any) => {
    let src = file.url;
    if (!src) {
      src = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
      });
    }
    const image = new Image();
    image.src = src;
    const imgWindow = window.open(src);
    imgWindow && imgWindow.document.write(image.outerHTML);
  };

  const onRemove = React.useCallback(
    (file: any) => {
      removeSelectedAsset && removeSelectedAsset(fileList);
      setFileList(undefined);
    },
    [fileList, removeSelectedAsset]
  );

  // TODO: Remove this dummy piece of code after API integration
  let fileObjectProp = {
    uploadType,
    fileName: uploadedFileName,
  };

  const uploadToS3 = React.useCallback(
    async ({ file, onError, onSuccess }) => {
      try {
        const response = await API.post(
          URL.postSelfieUploadGetUrl(user.empId, file.name),
          {}
        );
        const { presignedUploadUrl } = response.data;
        const data = await API.put(presignedUploadUrl, file, {
          "Content-Type": "binary/octet-stream",
        });
        const { status } = data;
        if (status === 200) {
          const finalCallResult = await API.post(
            URL.postUploadFinalSelfieVideoCall(_.get(user, "empId")),
            {
              id: response?.data?.id,
              uploadStatus: "SUCCESS",
            },
            {
              "Content-Type": "application/json",
            }
          );
          onSuccess(finalCallResult);
        }
      } catch (err) {
        onError(err);
      }
    },
    [user]
  );

  const uploadComponentFromANTD = React.useCallback(() => {
    if (uploadType === UploadTypes.SELFIE_VIDEO) {
      return (
        <Upload
          accept={allowedFilesType.join(",")}
          name="file_content"
          data={fileObjectProp}
          customRequest={uploadToS3}
          listType="picture-card"
          multiple={true}
          onChange={onChange}
          onPreview={onPreview}
          onRemove={onRemove}
        >
          {imageRenderProp(uploadType)}
        </Upload>
      );
    }
    return (
      <Upload
        accept={allowedFilesType.join(",")}
        name="file_content"
        data={fileObjectProp}
        listType="picture-card"
        multiple={uploadType === UploadTypes.PROFILE_PICTURE ? false : true}
        action={URL.postUploadEndPoint(_.get(user, "empId"))}
        onChange={onChange}
        beforeUpload={beforeUpload}
        onPreview={onPreview}
        onRemove={onRemove}
      >
        {imageRenderProp(uploadType)}
      </Upload>
    );
  }, [
    uploadToS3,
    user,
    allowedFilesType,
    fileObjectProp,
    uploadType,
    beforeUpload,
    onChange,
    onRemove,
  ]);

  const showUploadedAssets = React.useCallback(() => {
    return (
      uploadedFiles &&
      uploadedFiles.length > 0 &&
      uploadedFiles.map((uploadedData, key: number) => {
        const {
          fileType,
          thumbUrl,
          fileName,
          filename,
          cdnUrl,
          data,
        } = uploadedData;
        let downloadableLink: string =
          allowedFileTypes.videoFormat.includes(fileType) && cdnUrl
            ? cdnUrl
            : thumbUrl;

        const onAssetLinkClick = (
          data: string,
          filename: string,
          fileType: string
        ) => {
          browserDownload(data, filename, fileType);
        };

        const showComponentBasedonAssetType = () => {
          switch (fileType) {
            case assetExtensionsFromServer.pdf:
              return (
                <button
                  className="border-0 bg-none"
                  onClick={() => onAssetLinkClick(data, filename, fileType)}
                >
                  {fileName || filename}
                </button>
              );

            default:
              return (
                <a download href={downloadableLink}>
                  <div>{fileName || filename}</div>
                </a>
              );
          }
        };
        if (
          uploadType === (uploadedData.uploadType || fileType) &&
          uploadType !== UploadTypes.PROFILE_PICTURE
        ) {
          return (
            <div className="col p-0" key={key}>
              <div className="d-flex  my-3">
                <div className="p-2 ">
                  <img src={uploadedDocsIcon} alt="" />
                </div>
                <div className="p-2 "> {showComponentBasedonAssetType()}</div>
                <div className="ml-auto p-2 ">
                  {" "}
                  <button
                    className="border-0 bg-none"
                    onClick={() =>
                      removeSelectedAsset && removeSelectedAsset(uploadedData)
                    }
                  >
                    <div className="d-flex">
                      <img src={removeIcon} alt="" />
                    </div>
                  </button>
                </div>
              </div>
            </div>
          );
        }
        return <React.Fragment key={key}></React.Fragment>;
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedFiles]);

  return (
    <span
      css={[
        uploadStyle(uploadType, isMobileView, errorWhileUpload),
        responsiveStyles,
      ]}
    >
      <ShowMessage
        visible={isUploading}
        loading={isUploading}
        heading={"Uploading"}
        description={loadingScreenValues.ASSETS_LOADING_MESSAGE}
      />
      <div>{showUploadedAssets()}</div>
      <div className="d-flex align-items-center justify-content-center flex-column w-100">
        {isImagePresent && uploadType === UploadTypes.PROFILE_PICTURE ? (
          <React.Fragment></React.Fragment>
        ) : (
          <div className="d-flex justify-content-center align-items-center w-100">
            {uploadType === UploadTypes.PROFILE_PICTURE ? (
              <ImgCrop rotate zoom grid shape="rect">
                {uploadComponentFromANTD()}
              </ImgCrop>
            ) : (
              uploadComponentFromANTD()
            )}
          </div>
        )}

        {isImagePresent && uploadType === UploadTypes.PROFILE_PICTURE && (
          <div
            className={`d-flex justify-content-center align-items-center ${
              isMobileView ? "my-3" : ""
            }`}
          >
            <a
              download
              href={isImagePresent && isImagePresent.thumbUrl}
              className=""
            >
              <img
                src={isImagePresent.thumbUrl}
                alt={isImagePresent.filename || isImagePresent.name}
                className="preview-image-style"
              />
            </a>
            <button className="border-0 bg-none" onClick={onRemove}>
              <div className="d-flex">
                <img src={removeIcon} alt="" />
              </div>
            </button>
          </div>
        )}
      </div>
    </span>
  );
};
